// Hook PATCH per-field pour le DAL mobile.
// Chaque champ appelle `patch(path, value)` au onBlur (input/textarea) ou onChange
// (select/checkbox/toggle). Voir backend dal_patch.py pour la whitelist server-side.
//
// 🔴 N°546 — CONTRÔLE DE CONCURRENCE (révision serveur).
// Chaque réponse du serveur porte `revision`, la révision du DAL APRÈS l'écriture.
// Le hook la garde (`revisionRef`) et l'envoie en `base_revision` sur les PATCH
// CONDITIONNELS (remplacement d'une liste entière : `zones.<z>.travaux`,
// `selected_zones`) — mêmes règles que `dal_patch.patch_est_conditionnel`.
//
// ⚠️ Pour que la base soit TOUJOURS celle de la réponse précédente, les PATCH
// partent EN SÉRIE (une file) : deux coches rapides ⛔ ne peuvent plus se
// contredire entre elles (le second PATCH part avec la révision rendue par le
// premier). Sur un même path encore en attente, seule la DERNIÈRE valeur part.
// 409 (conflit) / 428 (révision requise) ⇒ statut `conflict`, message explicite :
// la saisie locale reste à l'écran, ⛔ rien n'est effacé.

import { useCallback, useRef, useState } from "react";
import { validatePatchPath, getDalAuthToken } from "../components/dal/dalState";

const API = process.env.REACT_APP_BACKEND_URL;

// Miroir de backend/metier_hub/tef/dal_patch.py:patch_est_conditionnel.
export function patchEstConditionnel(path) {
  if (path === "selected_zones") return true;
  const parts = (path || "").split(".");
  return parts.length === 3 && parts[0] === "zones" && parts[2] === "travaux";
}

export function messageDeConflit(detail) {
  if (detail && typeof detail === "object") return detail.message || detail.code || "Conflit de version";
  return detail || "Conflit de version";
}

export default function usePatchDAL({ dossierId, tenantId, zoneIds, initialRevision = 0 }) {
  const [status, setStatus] = useState("idle");          // idle | saving | saved | error | conflict
  const [errorMsg, setErrorMsg] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const revisionRef = useRef(Number(initialRevision) || 0);
  const chaineRef = useRef(Promise.resolve());            // la file série
  const enAttenteRef = useRef({});                         // { [path]: dernière valeur non partie }

  const envoyer = useCallback(async (path) => {
    if (!(path in enAttenteRef.current)) return true;      // coalescé par une valeur plus récente
    const value = enAttenteRef.current[path];
    delete enAttenteRef.current[path];
    const body = { path, value };
    if (patchEstConditionnel(path)) body.base_revision = revisionRef.current;
    try {
      const token = getDalAuthToken();
      const r = await fetch(`${API}/api/dal/save-mobile/${dossierId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": tenantId || "tef",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(body),
      });
      let j = null;
      try { j = await r.json(); } catch { /* corps vide */ }
      if (r.ok) {
        if (j && Number.isFinite(j.revision)) revisionRef.current = j.revision;
        setStatus("saved");
        setLastSavedAt(new Date());
        return true;
      }
      if (r.status === 409 || r.status === 428) {
        setStatus("conflict");
        setErrorMsg(messageDeConflit(j?.detail));
        return false;
      }
      setStatus("error");
      setErrorMsg((j && (j.detail || j.error)) || `${r.status}`);
      return false;
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "réseau");
      return false;
    }
  }, [dossierId, tenantId]);

  const patch = useCallback((path, value) => {
    if (!dossierId) return Promise.resolve(false);
    if (!validatePatchPath(path, zoneIds)) {
      setStatus("error");
      setErrorMsg(`Path invalide : ${path}`);
      return Promise.resolve(false);
    }
    setStatus("saving");
    setErrorMsg(null);
    enAttenteRef.current[path] = value;
    const p = chaineRef.current.then(() => envoyer(path));
    chaineRef.current = p.catch(() => false);
    return p;
  }, [dossierId, zoneIds, envoyer]);

  // Le POST d'enregistrement complet s'appuie sur la MÊME révision : il doit
  // partir APRÈS les PATCH en file, avec la révision qu'ils ont rendue.
  const attendreFile = useCallback(() => chaineRef.current, []);
  const getRevision = useCallback(() => revisionRef.current, []);
  const setRevision = useCallback((rev) => {
    if (Number.isFinite(rev)) revisionRef.current = rev;
  }, []);

  return { patch, status, lastSavedAt, errorMsg, attendreFile, getRevision, setRevision };
}
